/**
 * Bid Optimizer creation wizard — themed rebuild of the old antd-based
 * CreateBidOptimizer flow (pages/optimization/bid-optimizer/components/).
 * Same 3 steps (Upload → Configure → Review & Submit) and the same
 * dataMappingConfig/modelParameterConfig payload shape the backend
 * (app/modules/bid_multiplier/bid_multiplier.py) expects — only the chrome
 * changed, to match the Experiments/MMM design tokens.
 *
 * The jobConfigId is minted client-side by the landing page and lives in
 * the URL (/bid-optimizer/create/:id), matching the create/view pattern
 * used by Experiments and MMM.
 */

import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import StepUpload, { UploadStepData } from "./components/StepUpload";
import StepConfigure, { ConfigureStepData } from "./components/StepConfigure";
import StepReview from "./components/StepReview";

const STEPS = [
  { title: "Upload data",                    description: "Historic data upload" },
  { title: "Finalize dependent Variables",   description: "Take informed decision using various data points" },
  { title: "Review and Submit",              description: "Confirm and create job" },
];

export default function BidOptimizerCreate() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [uploadData, setUploadData] = useState<UploadStepData | null>(null);
  const [configureData, setConfigureData] = useState<ConfigureStepData | null>(null);
  const [jobName, setJobName] = useState("");

  if (!id) return null;

  return (
    <div style={{ display: "flex", height: "calc(100vh - 0px)", background: "var(--bg-base)", fontFamily: "var(--font-sans)" }}>
      {/* ── Step rail ── */}
      <div style={{
        width: 260, flexShrink: 0, padding: "28px 20px",
        background: "var(--bg-surface)", borderRight: "1px solid var(--border-default)",
        overflowY: "auto",
      }}>
        <button
          onClick={() => navigate("/bid-optimizer")}
          style={{
            marginBottom: 20, padding: "7px 14px", borderRadius: "var(--radius)",
            background: "none", color: "var(--text-tertiary)",
            border: "1px solid var(--border-default)", fontSize: 12,
            cursor: "pointer", fontFamily: "var(--font-sans)",
          }}
        >
          ← Back to list
        </button>

        <div
          title={jobName || "Bid Optimizer Job"}
          style={{
            fontSize: 16, fontWeight: 600, color: "var(--text-primary)",
            marginBottom: 28, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}
        >
          {jobName || "Bid Optimizer Job"}
        </div>

        {STEPS.map((step, idx) => {
          const state = idx < current ? "done" : idx === current ? "active" : "pending";
          return (
            <div key={step.title} style={{ display: "flex", gap: 12, position: "relative" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12, fontWeight: 600, flexShrink: 0,
                  background: state === "pending" ? "var(--bg-hover)" : "var(--blue)",
                  color: state === "pending" ? "var(--text-tertiary)" : "#fff",
                  border: state === "active" ? "2px solid var(--blue)" : "none",
                }}>
                  {state === "done" ? "✓" : idx + 1}
                </div>
                {idx < STEPS.length - 1 && (
                  <div style={{ width: 2, flex: 1, minHeight: 36, background: idx < current ? "var(--blue)" : "var(--border-default)" }} />
                )}
              </div>
              <div style={{ paddingBottom: 30 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: state === "pending" ? "var(--text-tertiary)" : "var(--text-primary)" }}>
                  {step.title}
                </div>
                <div style={{ fontSize: 11.5, color: "var(--text-tertiary)", marginTop: 2 }}>
                  {step.description}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Step content ── */}
      <div style={{ flex: 1, overflowY: "auto", padding: "32px 40px" }}>
        {current === 0 && (
          <StepUpload
            existingData={uploadData}
            onNext={data => { setUploadData(data); setCurrent(1); }}
          />
        )}
        {current === 1 && uploadData && (
          <StepConfigure
            uploadData={uploadData}
            existingData={configureData}
            onNameChange={setJobName}
            onPrevious={() => setCurrent(0)}
            onNext={data => { setConfigureData(data); setCurrent(2); }}
          />
        )}
        {current === 2 && uploadData && configureData && (
          <StepReview
            jobConfigId={id}
            uploadData={uploadData}
            configureData={configureData}
            onPrevious={() => setCurrent(1)}
            onCreated={() => navigate(`/bid-optimizer/${id}`)}
          />
        )}
      </div>
    </div>
  );
}
